import {useService} from '@cindedi/react-application';
import {H2} from '@cindedi/ui/components/Heading/H2';
import {truncate} from '@cindedi/utilities/truncate';
import type {MetaFunction, LoaderFunction} from '@remix-run/node';
import {json} from '@remix-run/node';
import {Link, useLoaderData} from '@remix-run/react';
import type {Collection} from 'collect.js';
import {PublicLayout} from '~/domains/organization/components/PublicLayout';

export interface MembrosLoaderData {
  members: Record<string, Organization.PublicMemberDto[]>;
}

export const meta: MetaFunction = function MembrosMeta() {
  return [
    {title: 'Membros - Cindedi'},
    {
      name: 'description',
      content:
        'Conheça os membros do CINDEDI! Explore o perfil de nossos pesquisadores e colaboradores que fortalecem nossas pesquisas e ações.',
    },
  ];
};

export const loader: LoaderFunction = async function MembrosLoader({context: {use}}) {
  const Member = use('Member');

  const members = await Member.all();

  return json({
    members: members
      .sortBy('positionId')
      .groupBy('position.name')
      .map((members) => (members as Collection<Organization.Member>).sortBy('name')),
  });
};

export default function MembrosView() {
  const {members} = useLoaderData<MembrosLoaderData>();
  useService('organization/titlebar').setTitle('Membros');

  return (
    <PublicLayout>
      <ul>
        {Object.entries(members).map(([position, members]) => (
          <li key={position} className="mb-8">
            <H2 size="sm" className="mb-4 md:mb-2">
              {position}
            </H2>
            <ul role="list" className="flex flex-col gap-2">
              {members.map((member) => (
                <li className="hover:bg-shade-3 bg-shade-2 rounded overflow-hidden" key={member.id}>
                  <Link to={`/membros/${member.slug}`}>
                    <div className="flex flex-col md:flex-row gap-2">
                      <div
                        role="img"
                        className="w-full md:w-20 h-44 md:h-20 max-h-full bg-shade-4 rounded bg-no-repeat bg-center bg-cover md:m-2"
                        style={{backgroundImage: `url(${member.cover.url})`}}
                        aria-label={member.name}
                      />
                      <div className="px-2 py-3 flex flex-col w-full">
                        <span className="font-semibold text-primary-12">{member.name}</span>
                        <div className="flex gap-2 text-shade-11 text-sm items-center mt-1">
                          {truncate(member.textDescription, 200)}
                        </div>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </PublicLayout>
  );
}
